import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { Drawer, Flex, Tabs, type TabsProps } from "antd";
import { createStyles } from "antd-style";

import { CompanyKnowledgeSettings } from "@av/apps/common-apps/platform/features/admin/company-knowledge/CompanyKnowledgeSettings";
import UsersSettings from "@av/apps/common-apps/platform/features/admin/settings/users/UsersSettings";
import type { PlatformAppEntry } from "@av/apps/common-apps/platform/services/apps/types";
import { Alert } from "@av/components/atoms/Alert";
import { Button } from "@av/components/atoms/Button";
import { List } from "@av/components/atoms/List";
import { Typography } from "@av/components/atoms/typography/Typography";
import { ForbiddenErrorPage } from "@av/components/templates/layouts/errors/types/ForbiddenErrorPage";
import { NotFoundPage } from "@av/components/templates/layouts/errors/types/NotFoundPage";
import { AdminSettingsSection } from "./AdminSettingsSection";
import {
  resolveAdminSettingsProductCode,
  useAdminSettingsAccess,
} from "./hooks/useAdminSettingsAccess";
import { useFetchAdminSettingsProductDetails } from "./hooks/useFetchAdminSettingsProductDetails";
import { useAdminSettingsNavigation } from "../../routes/useAdminSettingsNavigation";
import {
  getAdminSettingsAlerts,
  useCompanyAvailableApps,
} from "../../services/admin-settings/admin-settings";

type AdminSettingsPageProps = {
  apps: PlatformAppEntry[];
};

type AdminSettingsTab = NonNullable<TabsProps["items"]>[number];

const useStyles = createStyles(({ css, token }) => ({
  drawerBody: css`
    background: ${token.colorBgLayout};
    padding: ${token.paddingLG + token.padding}px ${token.paddingLG}px;
  `,
  tabs: css`
    & > .ant-tabs-nav {
      width: 166px;
    }

    &.ant-tabs-left > .ant-tabs-nav .ant-tabs-tab {
      text-align: left;
    }
  `,
  tabText: css`
    color: inherit;
    white-space: normal;
  `,
  productSection: css`
    padding: ${token.paddingXS}px 40px;
  `,
  productList: css`
    background: ${token.colorBgContainer};
    padding: 0 ${token.padding}px;
    width: 100%;
  `,
  productItem: css`
    && {
      padding: 14px 0;
    }
  `,
  productDetails: css`
    color: ${token.colorTextSecondary};
  `,
  productArrow: css`
    width: 50px;
  `,
}));

export function AdminSettingsPage(props: AdminSettingsPageProps) {
  const { apps } = props;
  const { t } = useTranslation();
  const { styles } = useStyles();

  const currentPath = useLocation();
  const currentProductCode = resolveAdminSettingsProductCode(currentPath.pathname);

  const [activeAdminSettingsTab, setActiveAdminSettingsTab] = useState("roles-and-permissions");
  const isAdminSettingsEnabled = useBooleanFlagValue("admin_settings", false);
  const isCompanyKnowledgeEnabled = useBooleanFlagValue("hr_company_knowledge", false);
  const isUsersManagementEnabled = useBooleanFlagValue("ats_users_management", false);

  const { isAdminSettingsOpen, closeAdminSettings, selectedSection, setAdminSettingsSection } =
    useAdminSettingsNavigation();
  const {
    canManageAtsRolesAndPermissions,
    canManageCompanyKnowledge,
    canManageCurrentProductRolesAndPermissions,
    isPermissionsLoading,
    adminPermittedApps,
    resolvedSelectedSection,
    productPermissions,
  } = useAdminSettingsAccess({
    apps,
    currentProductCode,
    isAdminSettingsOpen: isAdminSettingsEnabled && isAdminSettingsOpen,
    isCompanyKnowledgeEnabled,
    selectedSection,
  });

  const { data: companyAvailableApps = [] } = useCompanyAvailableApps(
    canManageCurrentProductRolesAndPermissions
  );
  const alerts = getAdminSettingsAlerts(t, companyAvailableApps);

  const productDetailsByPath = useFetchAdminSettingsProductDetails({
    enabled: isAdminSettingsEnabled && isAdminSettingsOpen,
    productPermissions,
  });

  const canAccessUsersTab = isUsersManagementEnabled && canManageAtsRolesAndPermissions;
  const availableProductPaths = new Set(apps.map((app) => app.path));

  const hasAnyAdminSettingsAccess =
    canManageCurrentProductRolesAndPermissions || canManageCompanyKnowledge || canAccessUsersTab;

  if (!isAdminSettingsOpen) {
    return null;
  }

  if (!isAdminSettingsEnabled) {
    return <ForbiddenErrorPage />;
  }

  if (isPermissionsLoading) {
    return null;
  }

  if (!hasAnyAdminSettingsAccess) {
    return <NotFoundPage />;
  }

  const adminSettingsTabs: AdminSettingsTab[] = [
    // The Users tab only ever renders ATS user data, so gate it on the
    // ATS permission to prevent showing it to users who lack ATS access.
    ...(canAccessUsersTab
      ? [
          {
            key: "users",
            label: (
              <Typography.Text
                testIdPrefix="admin-settings-left-nav-users"
                className={styles.tabText}
                strong
              >
                {t("platform.adminSettings.users")}
              </Typography.Text>
            ),
            children: (
              <Flex vertical gap="middle" flex="1 0 0" className={styles.productSection}>
                <UsersSettings availableProductPaths={availableProductPaths} />
              </Flex>
            ),
          },
        ]
      : []),
    ...(canManageCurrentProductRolesAndPermissions
      ? [
          {
            key: "roles-and-permissions",
            label: (
              <Typography.Text
                testIdPrefix="admin-settings-left-nav-roles-and-permissions"
                className={styles.tabText}
                strong
              >
                {t("platform.adminSettings.rolesAndPermissions")}
              </Typography.Text>
            ),
            children: (
              <Flex vertical gap="middle" flex="1 0 0" className={styles.productSection}>
                <Typography.Text
                  testIdPrefix="admin-settings-products-roles-and-permissions"
                  strong
                >
                  {t("platform.adminSettings.rolesAndPermissionsByProduct")}
                </Typography.Text>
                {resolvedSelectedSection === "products" ? (
                  <List
                    testIdPrefix="admin-settings-products"
                    dataSource={adminPermittedApps}
                    className={styles.productList}
                    renderItem={(app) => {
                      const productDetails = productDetailsByPath[app.path];

                      return (
                        <List.Item
                          key={app.path}
                          data-testid={`admin-settings-product-${app.path}`}
                          className={styles.productItem}
                        >
                          <Flex justify="space-between" align="stretch" flex="1 0 0">
                            <Flex vertical>
                              <Typography.Text
                                testIdPrefix={`admin-settings-product-${app.path}`}
                                strong
                              >
                                {app.productName}
                              </Typography.Text>
                              {productDetails && (
                                <Typography.Text
                                  testIdPrefix={`admin-settings-product-${app.path}-details`}
                                  className={styles.productDetails}
                                >
                                  {[
                                    t("platform.adminSettings.productDetails_users", {
                                      count: productDetails.userCount,
                                    }),
                                    t("platform.adminSettings.productDetails_securityGroups", {
                                      count: productDetails.securityGroupCount,
                                    }),
                                    t("platform.adminSettings.productDetails_roles", {
                                      count: productDetails.roleCount,
                                    }),
                                  ].join("; ")}
                                </Typography.Text>
                              )}
                            </Flex>
                            <Flex
                              vertical
                              justify="center"
                              align="flex-start"
                              className={styles.productArrow}
                            >
                              <Button
                                type="text"
                                onClick={() => {
                                  setAdminSettingsSection(app.path);
                                }}
                                aria-label={t("platform.adminSettings.navigateToSection", {
                                  productName: app.productName,
                                })}
                                testIdPrefix={"admin-settings-section-" + app.productName}
                              >
                                <RightOutlined />
                              </Button>
                            </Flex>
                          </Flex>
                        </List.Item>
                      );
                    }}
                  />
                ) : (
                  <AdminSettingsSection
                    selectedSection={resolvedSelectedSection}
                    onSectionChange={setAdminSettingsSection}
                    apps={adminPermittedApps}
                  />
                )}
                {resolvedSelectedSection === "products" &&
                  alerts.map((alert) => (
                    <Alert
                      testIdPrefix={"admin-settings-upsell-" + alert.product}
                      showIcon
                      closable
                      key={alert.product}
                      message={alert.message}
                      type="info"
                    />
                  ))}
              </Flex>
            ),
          },
        ]
      : []),
    ...(canManageCompanyKnowledge
      ? [
          {
            key: "company-knowledge",
            label: (
              <Typography.Text
                testIdPrefix="admin-settings-left-nav-company-knowledge"
                className={styles.tabText}
                strong
              >
                {t("platform.adminSettings.companyKnowledge")}
              </Typography.Text>
            ),
            children: (
              <Flex vertical gap="middle" flex="1 0 0" className={styles.productSection}>
                <CompanyKnowledgeSettings />
              </Flex>
            ),
          },
        ]
      : []),
  ];
  const defaultAdminSettingsTab = String(adminSettingsTabs[0]?.key ?? "company-knowledge");
  const resolvedActiveAdminSettingsTab = adminSettingsTabs.some(
    (tab) => tab.key === activeAdminSettingsTab
  )
    ? activeAdminSettingsTab
    : defaultAdminSettingsTab;

  return (
    <Drawer
      aria-label={t("platform.adminSettings.title")}
      data-testid="admin-settings-drawer"
      placement="top"
      open={isAdminSettingsOpen}
      onClose={closeAdminSettings}
      height="calc(100vh - 72px)"
      title={
        <Typography.Text testIdPrefix="admin-settings-title" strong>
          {t("platform.adminSettings.title")}
        </Typography.Text>
      }
      closeIcon={
        <CloseOutlined
          aria-hidden
          aria-label={t("platform.adminSettings.close")}
          data-testid="admin-settings-drawer-close"
        />
      }
      classNames={{
        body: styles.drawerBody,
      }}
      destroyOnHidden
    >
      <Tabs
        activeKey={resolvedActiveAdminSettingsTab}
        className={styles.tabs}
        data-testid="admin-settings-left-nav"
        onChange={setActiveAdminSettingsTab}
        items={adminSettingsTabs}
        tabPosition="left"
      />
    </Drawer>
  );
}
